import * as ko from "knockout";
import {IMessage, IMessageData, IObjectData, IRouteDefinitionParams, IRuleset} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import PromiseRequest from "../../../req/PromiseRequest";
import {createErrorMessage, getObjectDefinition, IObjectDefinition} from "../../../util/misc";
import {global, lm} from "../../../appContext";
import {getRouteDefinition} from "../../../constants/RouteDefinitions";
import {nsRoute} from "../../../util/route";
import {PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {MAX_CONCURRENT_REQUESTS, ObjectTypeId} from "../../../constants";
import {IObjectSearchParams} from "../../object/search";
import {ISelectedListActions} from "../../object/selected-list";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import wizardHtml from './wizard.html?raw';

export const template = wizardHtml;

enum Step {
    SelectObjects,
    Sending,
    Finish,
}

export class viewModel extends ParsedRouteDataDecorator {

    private readonly Step = Step;
    private readonly step: KnockoutObservable<Step | null> = ko.observable(null);
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly messageId = global.getRouteId();
    private readonly data: KnockoutObservable<IMessage | null> = ko.observable(null);

    // all users selected thorugh multi select
    private readonly selectedObjects: KnockoutObservableArray<IObjectData> = ko.observableArray<IObjectData>([]);

    private readonly numSuccess: KnockoutObservable<number | null> = ko.observable(null);
    private readonly numError: KnockoutObservable<number | null> = ko.observable(null);
    private readonly percentComplete: KnockoutObservable<number | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly req_getmessage = global.getPromiseRequest("getmessage");
    private readonly req_getruleset = global.getPromiseRequest("getruleset");

    private req_sendmessage_arr!: PromiseRequest[];
    private objectTypeId: ObjectTypeId = ObjectTypeId.User
    private objDef: IObjectDefinition | null = null
    private ruleset: IRuleset | null = null;

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.kosub.subscribe(this.step, () => this.onStep())

        this.init();
    }

    private onStep() {
        const id = 'sendmessage.wizard.info.' + this.step();
        let message = lm.t(id);
        if (message != null && message != id) {
            this.messageData({message: message})
        } else {
            this.messageData(null)
        }
    }

    private init() {
        this.messageData({message: REQUEST_MESSAGE});
        this.req_getmessage
            .request({id: global.getRouteId()})
            .then((data: IMessage[]) => {
                this.data(data[0] ?? null);
            })
            .then(() => {
                const message = this.data();
                if (!message) {
                    return;
                }
                const rulesetId = message.rulesetid;
                if (rulesetId > 0) {
                    return this.req_getruleset.request({id: rulesetId})
                        .then((data: [IRuleset]) => {
                            const ruleset = data[0];
                            this.ruleset = ruleset;
                            this.objectTypeId = ruleset.objecttype.id
                        });
                }
                this.objectTypeId = ObjectTypeId.User;
            })
            .then(() => {
                const objDef = getObjectDefinition(this.objectTypeId);
                this.objDef = objDef;
                this.messageData(null);
                this.step(Step.SelectObjects);
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
        ;
    }

    isEmail() {
        return !!this.data()?.messagetype?.ishtml;
    }

    onChange(vm: viewModel, e: JQuery.ChangeEvent) {
        this.selectedObjects([]);
    }

    badge = ko.pureComputed((): number => {
        return this.getObjectIds().length;
    });

    submitLabel = ko.pureComputed((): string => {
        const num = this.getObjectIds().length;
        let ret = `((send message))`;
        if (num) {
            ret += ` [${num}]`;
        }
        return ret;
    });

    private readonly selectedListActions = ko.pureComputed((): ISelectedListActions => ({
        quickActions: [{
            icon: 'fa-paper-plane',
            label: this.submitLabel,
            onClick: () => this.onSubmit(),
            enabled: () => !!this.selectedObjects().length,
        }],
    }));

    private getObjectIds = ko.pureComputed((): number[] => {
        return this.selectedObjects().map((user: IObjectData) => user.id);
    });

    onSubmit() {
        this.step(Step.Sending);
        this.messageData({message: PLEASE_WAIT_MESSAGE});
        this.percentComplete(null);
        this.req_sendmessage_arr = [];
        this.numSuccess(0);
        this.numError(0);
        const ids = this.getObjectIds()
        const numTotal = ids.length
        return new Promise((resolve: (value: unknown) => void) => {
            const doNext = (): Promise<any> => {
                const numSuccess = this.numSuccess() ?? 0;
                const numError = this.numError() ?? 0;
                const numProcessed = numSuccess + numError;
                this.percentComplete(numProcessed / numTotal);
                this.updateSendingMessage()
                if (!ids.length) {
                    if (numProcessed == numTotal) {
                        resolve(true)
                    }
                    return Promise.resolve()
                }
                const id = ids.shift()
                if (id == null) {
                    return Promise.resolve();
                }
                this.updateSendingMessage()
                const promiseRequest = new PromiseRequest("sendmessage");
                this.req_sendmessage_arr.push(promiseRequest);
                return promiseRequest
                    .request({
                        messageid: this.messageId,
                        targetobjectid: id,
                    })
                    .then(() => {
                        this.numSuccess((this.numSuccess() ?? 0) + 1);
                        return doNext()
                    })
                    .catch((reason: string) => {
                        this.numError((this.numError() ?? 0) + 1);
                        return doNext()
                    })

            }
            for (let i = 0; i < Math.min(numTotal, MAX_CONCURRENT_REQUESTS); i++) {
                doNext()
            }
        })
            .then(() => {
                this.messageData(null)
                this.step(Step.Finish)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
                this.step(Step.Finish)
            });
    }

    private updateSendingMessage() {
        this.messageData({message: this.getSentMessage(this.numSuccess() ?? 0)});
    }

    private getSentMessage(numSent: number, numTotal: number = this.selectedObjects().length) {
        const subject = this.objDef?.translation ?? '';
        return `message sent for number of total subjects|number:${numSent}|total:${numTotal}|s:((${subject}|ac|n:${numTotal}))`
    }

    private getNotSentMessage(numSent: number, numTotal: number = this.selectedObjects().length) {
        const subject = this.objDef?.translation ?? '';
        return `message not sent for number of total subjects because of error|number:${numSent}|total:${numTotal}|s:((${subject}|ac|n:${numTotal}))`
    }

    isStep(...steps: number[]): boolean {
        return steps.reduce((bool: boolean, step: number) => bool || step === this.step(), false);
    }

    private getSearchObjectParams(): IObjectSearchParams {
        const routeDef = getRouteDefinition('sendmessage');
        return {
            __objectTypeId: this.objectTypeId,
            isSelectable: true,
            selectedObjects: this.selectedObjects,
            __routeDef: this.__getParams({
                ...(routeDef ?? {}),
                showMultiOperations: false,
            }),
            autoSelect: true,
            selectedListActions: this.selectedListActions,
        }
    }

    override dispose() {
        this.kosub.unsuscribeAll();
        this.req_getmessage.abort();
        this.req_sendmessage_arr?.forEach((p: PromiseRequest) => p.abort());
        this.selectedListActions.dispose();
    }
}
